// People & employment (admin) — the source of truth for who is employed, when, and on what
// terms. Employment is a PERIOD list, not a flag: seasonal people get rehired and terms change
// mid-employment, so each spell keeps its own row. Nobody is ever deleted — ending employment
// keeps every picked/packed record and keeps the username claimed.
import React, { useEffect, useState } from 'react';
import { authFetch, API } from './api';

interface Period {
  id: number; username: string; start_date: string; end_date: string | null;
  contract_type: string; day_accounting: boolean; fulfilment: string | null;
  worker_type: string; country: string; company: string | null;
  default_shift_id: number | null; weekly_days: number[]; monthly_hours: number | null;
  tracked: boolean; probation_end: string | null; end_reason: string | null; note: string | null;
  resignation_announced_at: string | null; resignation_announced_by: string | null;
  notice_days: number | null; notice_basis: string | null;
  statutory_last_day: string | null; agreed_last_day: string | null;
  resignation_note: string | null;
}
interface Login { username: string; role: string; active: boolean; }
interface Person {
  username: string; display_name: string; email: string | null; role: string;
  account_active: boolean; account_created: string | null;
  removed_at?: string | null; former?: boolean; needs_setup?: boolean;
  // Their last day has passed but the account is still open — offboarding is unfinished.
  needs_offboarding?: boolean;
  periods: Period[]; current: Period | null; employed: boolean;
  // A person may hold more than one login — e.g. a manager with a warehouse-floor account so a
  // shared terminal is not left sitting in their management view. Work done on any of them is
  // this person's work.
  logins?: Login[];
}
interface Shift { id: number; name: string; start_time: string; end_time: string; }
interface Ev {
  id: number; event_type: string; note: string | null;
  created_at: string; created_by: string; old_value: any; new_value: any;
}

const WD = [[1, 'Mo'], [2, 'Tu'], [3, 'We'], [4, 'Th'], [5, 'Fr'], [6, 'Sa'], [7, 'Su']] as const;

const CONTRACT_LABEL: Record<string, string> = {
  fixed_weekly: 'Fixed weekly days',
  monthly_hours: 'Monthly hours',
  planned_dates: 'Agreed dates',
  on_call: 'On call / casual',
};
const CONTRACT_HINT: Record<string, string> = {
  fixed_weekly: 'Same weekdays every week. Days AND hours are both tracked.',
  monthly_hours: 'A monthly hour total, days flexible. Hours only — no day accounting.',
  planned_dates: 'Specific dates agreed in advance. Days AND hours are tracked.',
  on_call: 'No commitment. Hours only — everything worked is extra.',
};
const END_REASONS = [['resigned', 'Resigned'], ['contract_ended', 'Contract ended'],
                     ['dismissed', 'Dismissed'], ['other', 'Other']] as const;
const CHECKLIST = [['scanner_returned', 'Scanner returned'], ['keys_returned', 'Keys / badge returned'],
                   ['access_revoked', 'Other access revoked'], ['hours_settled', 'Final hours settled']] as const;

const inp = 'bg-gray-800 border border-gray-700 text-gray-200 text-sm rounded px-2 py-1 focus:outline-none focus:border-blue-500';
const btn = 'px-3 py-1.5 text-xs rounded-lg font-medium';

const fmt = (d: string | null) => d || '—';

// Three tables rather than one, because these are three different jobs. Permanent staff get their
// shift and weekdays maintained; temporary people get their dates or hour totals agreed; management
// needs nothing done to them at all. Nobody is assigned by hand — the group falls out of terms that
// already exist, so it can never disagree with the contract:
//   not tracked            -> management  (that flag already means "never appears in work time")
//   contract != fixed_weekly -> temporary  (agreed dates / monthly hours / on call)
//   otherwise              -> hired
type Group = 'leaving' | 'hired' | 'temp' | 'mgmt';

const groupOf = (p: Person): Group => {
  // Anyone whose last day has gone by but whose login still works comes first: that is an
  // outstanding job, not a category of worker.
  if (p.needs_offboarding) return 'leaving';
  const c = p.current || p.periods[0];
  if (c && !c.tracked) return 'mgmt';
  if (c && c.contract_type !== 'fixed_weekly') return 'temp';
  return 'hired';
};

type Col = 'person' | 'contract' | 'employed' | 'type' | 'shift' | 'days' | 'commitment'
         | 'role' | 'status' | 'actions';

const GROUPS: { key: Group; title: string; blurb: string; cols: Col[]; empty: string }[] = [
  {
    key: 'leaving', title: 'Left — still to close off',
    blurb: 'Their last day has passed, but the account is still open and they can still sign in. '
         + 'Recording notice deliberately does not switch the login off — they are working it — so '
         + 'this is the last step. Press End to close the employment, take their access away and '
         + 'record the leaver checklist. Nothing is deleted: their hours, picking and exports all '
         + 'stay exactly as they are.',
    cols: ['person', 'employed', 'type', 'status', 'actions'],
    empty: 'Nobody is waiting to be closed off.',
  },
  {
    key: 'hired', title: 'Hired workers',
    blurb: 'Permanent staff on the same weekdays every week. Days and hours are both tracked, so '
         + 'these are the people whose shift and working days you maintain here.',
    cols: ['person', 'employed', 'type', 'shift', 'days', 'status', 'actions'],
    empty: 'Nobody on fixed weekly days.',
  },
  {
    key: 'temp', title: 'Temporary workers',
    blurb: 'Anyone not on fixed weekly days — agreed dates, a monthly hour total, or on call. What '
         + 'they owe is set per person, so they are the ones needing attention month to month. '
         + 'Agreed dates are picked on the Calendar tab.',
    cols: ['person', 'contract', 'employed', 'type', 'shift', 'commitment', 'status', 'actions'],
    empty: 'No temporary workers.',
  },
  {
    key: 'mgmt', title: 'Management — no managing needed',
    blurb: 'They keep their login but never appear in the calendar, dashboard or exports, so there '
         + 'is nothing to keep up to date. Move somebody in or out of here with “Track work time” '
         + 'under Terms.',
    cols: ['person', 'role', 'employed', 'type', 'status', 'actions'],
    empty: 'Everybody is tracked.',
  },
];

const COL_LABEL: Record<Col, string> = {
  person: 'Person', contract: 'Contract', employed: 'Employed', type: 'Type', shift: 'Shift',
  days: 'Work days', commitment: 'Commitment', role: 'Role', status: 'Status', actions: '',
};

export default function EmploymentPanel() {
  const [people, setPeople] = useState<Person[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Former workers are NOT listed here — the table would fill up with ex-staff. They are
  // fetched on demand only to rehire someone. Their history stays intact in the calendar,
  // exports and P&P statistics for the months they actually worked.
  const [formerOpen, setFormerOpen] = useState(false);
  const [former, setFormer] = useState<Person[]>([]);

  // one open drawer at a time: 'terms' | 'end' | 'rehire' | 'history'
  const [drawer, setDrawer] = useState<{ kind: string; person: Person } | null>(null);
  const [events, setEvents] = useState<Ev[]>([]);

  const load = async () => {
    setErr(null);
    const r = await authFetch(`${API}/statistics/employment`);
    if (!r.ok) { setErr('Could not load people'); return; }
    const d = await r.json();
    setPeople(d.people || []);
    setShifts(d.shifts || []);
  };
  useEffect(() => { load(); }, []);

  const call = async (url: string, method: string, body?: any) => {
    setBusy(url); setErr(null);
    const r = await authFetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    setBusy(null);
    if (!r.ok) {
      let msg = `${r.status}`;
      try { msg = (await r.json()).detail || msg; } catch { /* keep status */ }
      setErr(msg);
      return null;
    }
    await load();
    return r.json().catch(() => ({}));
  };

  const loadFormer = async () => {
    const r = await authFetch(`${API}/statistics/employment?include_former=1`);
    if (!r.ok) { setErr('Could not load former workers'); return; }
    const d = await r.json();
    const hereNow = new Set(people.map(x => x.username));
    setFormer((d.people || []).filter((x: Person) => !hereNow.has(x.username)));
  };

  const openHistory = async (p: Person) => {
    setDrawer({ kind: 'history', person: p });
    const r = await authFetch(`${API}/statistics/employment/${encodeURIComponent(p.username)}/events`);
    setEvents(r.ok ? await r.json() : []);
  };

  // The endpoint already returns only people who are currently here.
  const employed = people;

  const shiftName = (id: number | null) => shifts.find(s => s.id === id)?.name || '—';

  const cell = (p: Person, col: Col) => {
    const c = p.current || p.periods[0];
    switch (col) {
      case 'person':
        return (
          <td key={col} className="px-3 py-2.5">
            <div className="text-gray-200">{p.display_name}</div>
            <div className="text-[11px] text-gray-600">
              {p.username}
              {!p.account_active && <span className="ml-2 text-amber-500/80 uppercase">login off</span>}
            </div>
            {!!p.logins?.length && (
              <div className="text-[10px] text-blue-400/70 mt-0.5"
                   title={'Also logs in as ' + p.logins.map(l => l.username).join(', ')
                          + ' — work done on those accounts counts as this person’s work'}>
                + {p.logins.map(l => l.username).join(', ')}
              </div>
            )}
          </td>
        );
      case 'contract':
        return (
          <td key={col} className="px-3 py-2.5">
            {c ? (
              <span className="text-[11px] px-2 py-0.5 rounded bg-gray-800 text-gray-300"
                    title={CONTRACT_HINT[c.contract_type]}>
                {CONTRACT_LABEL[c.contract_type] || c.contract_type}
              </span>
            ) : <span className="text-gray-600 text-xs">never employed</span>}
          </td>
        );
      case 'employed':
        return (
          <td key={col} className="px-3 py-2.5 text-gray-400 text-xs whitespace-nowrap">
            {p.needs_setup
              ? <span className="text-amber-400">start date not set</span>
              : c ? fmt(c.start_date) : '—'}
            {c?.end_date && <span className="text-gray-600"> → {c.end_date}</span>}
            {p.periods.length > 1 && (
              <span className="ml-1.5 text-[10px] text-blue-400/70">{p.periods.length} spells</span>
            )}
          </td>
        );
      case 'type':
        return (
          <td key={col} className="px-3 py-2.5 text-gray-400 text-xs">
            {c ? (c.worker_type === 'office' ? 'Office' : 'Warehouse') : '—'}
            {c && (c.company
              ? <span className="block text-[10px] text-gray-500 mt-0.5" title={`Employed by ${c.company}`}>{c.company}</span>
              : <span className="block text-[10px] text-amber-500/80 mt-0.5"
                      title="No company set — this person is left out of the accountant’s absence table">
                  no company
                </span>)}
          </td>
        );
      case 'shift':
        return <td key={col} className="px-3 py-2.5 text-gray-400 text-xs">{c ? shiftName(c.default_shift_id) : '—'}</td>;
      case 'days':
        return (
          <td key={col} className="px-3 py-2.5">
            <div className="flex gap-0.5">
              {WD.map(([d, lbl]) => (
                <span key={d} className={`w-5 h-5 text-[9px] rounded flex items-center justify-center ${
                  c?.weekly_days?.includes(d) ? 'bg-blue-600/80 text-white' : 'bg-gray-800 text-gray-600'}`}>{lbl}</span>
              ))}
            </div>
          </td>
        );
      case 'commitment':
        // What this person actually owes, which is the whole point of the temporary table and
        // differs per contract type.
        return (
          <td key={col} className="px-3 py-2.5 text-xs text-gray-400">
            {!c ? '—'
              : c.contract_type === 'monthly_hours'
                ? (c.monthly_hours ? <span>{c.monthly_hours} h / month</span>
                                   : <span className="text-red-400">no monthly total set</span>)
              : c.contract_type === 'planned_dates'
                ? <span>agreed dates
                    <span className="block text-[10px] text-gray-600">
                      {c.fulfilment === 'threshold' ? 'part of a day counts' : 'must be worked in full'}
                    </span>
                  </span>
              : <span className="text-gray-500">no commitment</span>}
            {!c.day_accounting && (
              <span className="block text-[10px] text-gray-600" title="Hours only — day accounting does not apply">
                hours only
              </span>
            )}
          </td>
        );
      case 'role':
        return <td key={col} className="px-3 py-2.5 text-gray-400 text-xs capitalize">{p.role || '—'}</td>;
      case 'status':
        return (
          <td key={col} className="px-3 py-2.5 text-xs">
            {c?.resignation_announced_at ? (
              <span className="text-amber-400" title={
                `Notice given ${c.resignation_announced_at.slice(0, 16)} by ${c.resignation_announced_by || '?'}`
                + ` — ${c.notice_days} days (${c.notice_basis === 'probation' ? 'katseaeg' : 'ordinary'})`
                + (c.agreed_last_day && c.agreed_last_day !== c.statutory_last_day
                   ? `; statutory last day was ${c.statutory_last_day}` : '')}>
                resigned · last day {c.agreed_last_day || c.statutory_last_day}
                {c.agreed_last_day && c.statutory_last_day && c.agreed_last_day !== c.statutory_last_day && (
                  <span className="text-gray-500"> (agreed)</span>
                )}
              </span>
            ) : c?.end_reason ? (
              <span className="text-gray-500">
                {END_REASONS.find(([v]) => v === c.end_reason)?.[1] || c.end_reason}
              </span>
            ) : ''}
          </td>
        );
      case 'actions':
        return (
          <td key={col} className="px-3 py-2.5">
            <div className="flex flex-wrap gap-1.5 justify-end">
              <button onClick={() => setDrawer({ kind: 'terms', person: p })}
                className={`${btn} bg-gray-800 hover:bg-gray-700 text-gray-300`}>Terms</button>
              {p.employed && (
                <button onClick={() => setDrawer({ kind: 'notice', person: p })}
                  className={`${btn} ${c?.resignation_announced_at
                    ? 'bg-amber-800/70 hover:bg-amber-700 text-white'
                    : 'bg-gray-800 hover:bg-gray-700 text-gray-300'}`}>
                  {c?.resignation_announced_at ? 'Notice ✓' : 'Notice'}
                </button>
              )}
              {p.needs_setup
                ? <button onClick={() => setDrawer({ kind: 'rehire', person: p })}
                    className={`${btn} bg-amber-700 hover:bg-amber-600 text-white`}>
                    Set start date
                  </button>
                : p.needs_offboarding
                ? <button onClick={() => setDrawer({ kind: 'end', person: p })}
                    title="Their last day has passed and they can still sign in — close it off"
                    className={`${btn} bg-red-800 hover:bg-red-700 text-white`}>End</button>
                : p.employed
                ? <button onClick={() => setDrawer({ kind: 'end', person: p })}
                    className={`${btn} bg-gray-800 hover:bg-red-900/60 text-gray-400 hover:text-red-300`}>End</button>
                : <button onClick={() => setDrawer({ kind: 'rehire', person: p })}
                    className={`${btn} bg-emerald-700/70 hover:bg-emerald-700 text-white`}>Rehire</button>}
              <button onClick={() => openHistory(p)}
                className={`${btn} bg-gray-800 hover:bg-gray-700 text-gray-400`}>History</button>
            </div>
          </td>
        );
      default:
        return null;
    }
  };

  const table = (g: typeof GROUPS[number], rows: Person[]) => (
    <div key={g.key}>
      <div className="mb-1.5 px-1">
        <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold">
          {g.title} ({rows.length})
        </div>
        <p className="text-[11px] text-gray-600 mt-1 max-w-3xl">{g.blurb}</p>
      </div>
      <div className="bg-[#1a1d27] rounded-xl border border-gray-800 overflow-x-auto">
        {rows.length === 0 ? (
          <p className="text-xs text-gray-600 px-4 py-3">{g.empty}</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-500 text-xs border-b border-gray-800">
                {g.cols.map(col => (
                  <th key={col} className="px-3 py-2 text-left font-medium">{COL_LABEL[col]}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(p => (
                <tr key={p.username}
                    className={`border-b border-gray-800/50 ${p.employed ? '' : 'opacity-60'}`}>
                  {g.cols.map(col => cell(p, col))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold">
          People — currently here ({employed.length})
        </div>
        <button onClick={() => { const next = !formerOpen; setFormerOpen(next); if (next) loadFormer(); }}
          className="text-xs text-gray-500 hover:text-gray-300">
          {formerOpen ? '▾' : '▸'} Rehire a former worker
        </button>
      </div>

      {err && (
        <div className="mb-2 bg-red-950/40 border border-red-900/60 text-red-300 text-xs rounded-lg px-3 py-2">
          {err}
        </div>
      )}

      <div className="space-y-6">
        {GROUPS.map(g => table(g, employed.filter(p => groupOf(p) === g.key)))}
      </div>

      {formerOpen && (
        <div className="mt-3 bg-[#1a1d27] rounded-xl border border-gray-800 p-4">
          <p className="text-[11px] text-gray-500 mb-3">
            People who have left. They are kept out of the table above so it stays about who is here
            now — but every record of their work remains, and they still appear in the calendar,
            exports and P&amp;P statistics for the months they actually worked.
          </p>
          {former.length === 0 ? (
            <p className="text-xs text-gray-600">Nobody has left yet.</p>
          ) : (
            <div className="space-y-1.5">
              {former.map(p => (
                <div key={p.username} className="flex items-center gap-3 flex-wrap">
                  <span className="text-sm text-gray-300 min-w-[150px]">{p.display_name}</span>
                  <span className="text-[11px] text-gray-600">{p.username}</span>
                  <span className="text-[11px] text-gray-600">
                    {p.periods.length > 0 && p.periods[0].end_date ? 'left ' + p.periods[0].end_date : ''}
                  </span>
                  <button onClick={() => setDrawer({ kind: 'rehire', person: p })}
                    className={btn + ' ml-auto bg-emerald-700/70 hover:bg-emerald-700 text-white'}>
                    Rehire
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {drawer && (
        <Drawer person={drawer.person} kind={drawer.kind} shifts={shifts} events={events}
          busy={!!busy} onClose={() => { setDrawer(null); setErr(null); }} call={call} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------

function Drawer({ person, kind, shifts, events, busy, onClose, call }: {
  person: Person; kind: string; shifts: Shift[]; events: Ev[]; busy: boolean;
  onClose: () => void; call: (u: string, m: string, b?: any) => Promise<any>;
}) {
  const c = person.current || person.periods[0] || null;
  const today = new Date().toISOString().slice(0, 10);

  // terms / rehire form state
  const [ct, setCt] = useState(c?.contract_type || 'fixed_weekly');
  const [wt, setWt] = useState(c?.worker_type || 'warehouse');
  const [shift, setShift] = useState<number | null>(c?.default_shift_id ?? (shifts[0]?.id ?? null));
  const [days, setDays] = useState<number[]>(c?.weekly_days || [1, 2, 3, 4, 5]);
  const [mh, setMh] = useState<string>(c?.monthly_hours != null ? String(c.monthly_hours) : '');
  const [tracked, setTracked] = useState(c?.tracked ?? true);
  const [dayAcct, setDayAcct] = useState(c?.day_accounting ?? true);
  // How strictly a day must be worked to count. Blank = the default for the contract type:
  // a regular weekday only has to clear the threshold, an AGREED date must be worked in full.
  const [ful, setFul] = useState<string>(c?.fulfilment || '');
  // Legal entity. Blank is a real choice, not an oversight: it keeps someone (e.g. a temporary
  // worker) off the accountant’s absence hand-off while every other report still covers them.
  const [company, setCompany] = useState<string>(c?.company || '');
  const [companies, setCompanies] = useState<string[]>([]);
  const [newCo, setNewCo] = useState('');
  const [eff, setEff] = useState(today);
  const [note, setNote] = useState('');
  const [correct, setCorrect] = useState(false);   // fix in place vs. new spell
  const [startDate, setStartDate] = useState(c?.start_date || today);

  // resignation form state
  const [annAt, setAnnAt] = useState(new Date().toISOString().slice(0, 16));
  const [agreedLast, setAgreedLast] = useState('');
  const [preview, setPreview] = useState<any>(null);

  // end form state. Somebody who gave notice already HAS a last day, agreed or statutory — default
  // to it rather than to today, or closing them off a week later would quietly move their leaving
  // date and with it the hours they were expected to work.
  const [endDate, setEndDate] = useState(
    c?.agreed_last_day || c?.statutory_last_day || c?.end_date || today);
  const [reason, setReason] = useState(c?.end_reason || 'resigned');
  const [checks, setChecks] = useState<Record<string, boolean>>({});

  const toggleDay = (d: number) =>
    setDays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d].sort());

  // The known entities live in work-time settings so the spelling stays identical everywhere —
  // the export groups by this exact string.
  useEffect(() => {
    if (kind !== 'terms' && kind !== 'rehire') return;
    authFetch(`${API}/statistics/work-time/settings`)
      .then(r => r.ok ? r.json() : null)
      .then(s => { if (s?.companies) setCompanies(s.companies); });
  }, [kind]);

  // Ask the server what the statute gives, so the rule lives in exactly one place.
  useEffect(() => {
    if (kind !== 'notice' || !c) return;
    const on = (annAt || '').slice(0, 10);
    if (!on) return;
    authFetch(`${API}/statistics/employment/${c.id}/notice-preview?announced_on=${on}`)
      .then(r => r.ok ? r.json() : null)
      .then(p => { if (p) setPreview(p); });
  }, [kind, c, annAt]);

  const terms = () => ({
    contract_type: ct, worker_type: wt, default_shift_id: shift, weekly_days: days,
    monthly_hours: ct === 'monthly_hours' && mh ? parseFloat(mh) : null,
    tracked, day_accounting: dayAcct, fulfilment: ful || null, note: note || null,
    company: (newCo.trim() || company) || null,
  });

  const submit = async () => {
    // A brand-new entity name is added to the shared list first, so the next person can pick it
    // instead of retyping it (and mistyping it into a second block on the sheet).
    const fresh = newCo.trim();
    if (fresh && !companies.includes(fresh)) {
      await authFetch(`${API}/statistics/work-time/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companies: [...companies, fresh] }),
      });
    }
    if (kind === 'end') {
      // /retire, not /deactivate. deactivate only PAUSES an account — it ignores the end date,
      // the reason and the leaver checklist entirely, so ending someone quietly threw all three
      // away and left their employment period open.
      await call(`${API}/users/${encodeURIComponent(person.username)}/retire`, 'POST', {
        end_date: endDate, end_reason: reason, note: note || null, leaver_checklist: checks,
      });
    } else if (kind === 'notice') {
      if (!c) return;
      await call(`${API}/statistics/employment/${c.id}/resign`, 'POST', {
        announced_at: annAt, agreed_last_day: agreedLast || null, note: note || null,
      });
    } else if (kind === 'rehire') {
      await call(`${API}/statistics/employment`, 'POST', { username: person.username, start_date: startDate, ...terms() });
    } else if (kind === 'terms') {
      if (!c) return;
      if (correct) await call(`${API}/statistics/employment/${c.id}`, 'PUT', { start_date: startDate, ...terms() });
      else         await call(`${API}/statistics/employment/${c.id}/change-terms`, 'POST', { effective_from: eff, ...terms() });
    }
    onClose();
  };

  const TermsFields = (
    <>
      <label className="block">
        <span className="text-xs text-gray-500">Contract type</span>
        <select className={`${inp} w-full mt-1`} value={ct} onChange={e => {
          const v = e.target.value; setCt(v);
          setDayAcct(v === 'fixed_weekly' || v === 'planned_dates');
        }}>
          {Object.entries(CONTRACT_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <span className="block text-[11px] text-gray-600 mt-1">{CONTRACT_HINT[ct]}</span>
      </label>
      {ct === 'monthly_hours' && (
        <label className="block">
          <span className="text-xs text-gray-500">Hours per month</span>
          <input type="number" min={0} step={0.5} className={`${inp} w-full mt-1`} value={mh}
            onChange={e => setMh(e.target.value)} placeholder="e.g. 60" />
        </label>
      )}
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs text-gray-500">Type</span>
          <select className={`${inp} w-full mt-1`} value={wt} onChange={e => setWt(e.target.value)}>
            <option value="warehouse">Warehouse</option>
            <option value="office">Office</option>
          </select>
        </label>
        <label className="block">
          <span className="text-xs text-gray-500">Default shift</span>
          <select className={`${inp} w-full mt-1`} value={shift ?? ''}
            onChange={e => setShift(e.target.value ? parseInt(e.target.value) : null)}>
            {shifts.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
      </div>
      <label className="block">
        <span className="text-xs text-gray-500">Company (legal entity)</span>
        <select className={`${inp} w-full mt-1`} value={newCo ? '__new' : company}
          onChange={e => {
            const v = e.target.value;
            if (v === '__new') { setNewCo(' '); } else { setNewCo(''); setCompany(v); }
          }}>
          <option value="">— not in the accountant’s absence table —</option>
          {companies.map(co => <option key={co} value={co}>{co}</option>)}
          {company && !companies.includes(company) && <option value={company}>{company}</option>}
          <option value="__new">+ New company…</option>
        </select>
        {newCo !== '' && (
          <input className={`${inp} w-full mt-1.5`} value={newCo.trim() === '' ? '' : newCo}
            autoFocus placeholder="Company name as the accountant writes it"
            onChange={e => setNewCo(e.target.value || ' ')} />
        )}
        <span className="block text-[11px] text-gray-600 mt-1">
          Groups the accountant’s absence table. Leave blank to keep this person off that sheet —
          they still appear in the calendar, hours exports and year review.
        </span>
      </label>
      <div>
        <span className="text-xs text-gray-500">Work days</span>
        <div className="flex gap-1 mt-1">
          {WD.map(([d, lbl]) => (
            <button key={d} onClick={() => toggleDay(d)}
              className={`w-7 h-7 text-[10px] rounded ${days.includes(d) ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-500'}`}>{lbl}</button>
          ))}
        </div>
      </div>
      <div className="flex gap-4">
        <label className="flex items-center gap-2 text-xs text-gray-400">
          <input type="checkbox" checked={tracked} onChange={e => setTracked(e.target.checked)} />
          Track work time
        </label>
        <label className="flex items-center gap-2 text-xs text-gray-400" title="Off = hours only, no day counting">
          <input type="checkbox" checked={dayAcct} onChange={e => setDayAcct(e.target.checked)} />
          Count days
        </label>
      </div>
      {dayAcct && (
        <label className="block">
          <span className="text-xs text-gray-500">A day counts when</span>
          <select className={`${inp} w-full mt-1`} value={ful} onChange={e => setFul(e.target.value)}>
            <option value="">
              Default for this contract ({ct === 'planned_dates' ? 'full day' : 'threshold'})
            </option>
            <option value="threshold">The threshold is met — a part-day still counts</option>
            <option value="full_day">The full day is worked — a part-day does not count</option>
          </select>
          <span className="block text-[11px] text-gray-600 mt-1">
            {(ful || (ct === 'planned_dates' ? 'full_day' : 'threshold')) === 'full_day'
              ? 'They agreed to this specific day, so half of it does not fulfil it. A part-day shows as missed AND as hours owed.'
              : 'Clearing the threshold counts the day; any shortfall is settled by the hours ledger and work-back.'}
          </span>
        </label>
      )}
    </>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 overflow-y-auto py-10"
         onClick={onClose}>
      <div className="bg-[#151822] border border-gray-800 rounded-2xl w-full max-w-lg p-5 space-y-4"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-white font-semibold">
              {kind === 'end' ? 'End employment'
               : kind === 'rehire' ? (person.needs_setup ? 'When did they start?' : 'Rehire') :
               kind === 'notice' ? 'Resignation notice' :
               kind === 'history' ? 'Employment history' : 'Employment terms'}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">{person.display_name} · {person.username}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white text-lg leading-none">×</button>
        </div>

        {kind === 'history' && (
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {person.periods.length > 0 && (
              <div className="bg-[#1a1d27] border border-gray-800 rounded-xl p-3">
                <div className="text-[11px] text-gray-500 uppercase tracking-wide mb-1.5">Periods</div>
                {person.periods.map(p => (
                  <div key={p.id} className="text-xs text-gray-300 flex justify-between py-0.5">
                    <span>{p.start_date} → {p.end_date || 'present'}</span>
                    <span className="text-gray-500">{CONTRACT_LABEL[p.contract_type] || p.contract_type}</span>
                  </div>
                ))}
              </div>
            )}
            {events.length === 0 && <p className="text-gray-600 text-xs">No recorded events.</p>}
            {events.map(e => (
              <div key={e.id} className="bg-[#1a1d27] border border-gray-800 rounded-lg px-3 py-2">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-200">{e.event_type.replace(/_/g, ' ')}</span>
                  <span className="text-gray-600">{e.created_at.slice(0, 16)}</span>
                </div>
                <div className="text-[11px] text-gray-500 mt-0.5">by {e.created_by}</div>
                {e.note && <div className="text-[11px] text-gray-600 mt-1 italic">{e.note}</div>}
              </div>
            ))}
          </div>
        )}

        {kind === 'notice' && c && (
          <div className="space-y-3">
            {c.resignation_announced_at ? (
              <div className="bg-amber-950/20 border border-amber-900/40 rounded-xl p-3 text-xs">
                <div className="text-amber-300">
                  Notice already recorded {c.resignation_announced_at.slice(0, 16)}
                  {c.resignation_announced_by ? ` by ${c.resignation_announced_by}` : ''}
                </div>
                <div className="text-gray-400 mt-1">
                  {c.notice_days} days ({c.notice_basis === 'probation' ? 'katseaeg' : 'ordinary'}) ·
                  statutory last day {c.statutory_last_day}
                  {c.agreed_last_day && c.agreed_last_day !== c.statutory_last_day
                    && ` · agreed ${c.agreed_last_day}`}
                </div>
                {c.resignation_note && <div className="text-gray-500 mt-1 italic">{c.resignation_note}</div>}
                <button
                  onClick={async () => { await call(`${API}/statistics/employment/${c.id}/resign`, 'DELETE'); onClose(); }}
                  className={`${btn} bg-gray-800 hover:bg-red-900/60 text-gray-400 hover:text-red-300 mt-2`}>
                  Withdraw notice
                </button>
              </div>
            ) : null}

            <label className="block">
              <span className="text-xs text-gray-500">When they announced it</span>
              <input type="datetime-local" className={`${inp} w-full mt-1`} value={annAt}
                onChange={e => setAnnAt(e.target.value)} />
            </label>

            {preview && (
              <div className="bg-[#1a1d27] border border-gray-800 rounded-xl p-3 text-xs space-y-1">
                <div className="text-gray-300">
                  {preview.in_probation
                    ? `Still in katseaeg (until ${preview.probation_end}) → `
                    : `Past katseaeg (ended ${preview.probation_end}) → `}
                  <span className="text-white font-medium">{preview.notice_days} calendar days</span>
                </div>
                <div className="text-gray-400">
                  Statutory last day <span className="text-gray-200">{preview.statutory_last_day}</span>
                </div>
                {preview.probation_assumed && (
                  <div className="text-amber-500/80">
                    Katseaeg date is assumed — {4} months from {preview.start_date}, the statutory
                    maximum. If their contract says otherwise, set it under Terms first.
                  </div>
                )}
                <div className="text-gray-600 pt-1 border-t border-gray-800/60">
                  Töölepingu seadus: 15 days during katseaeg (§96), 30 days after (§98).
                </div>
              </div>
            )}

            <label className="block">
              <span className="text-xs text-gray-500">Agreed last day (optional)</span>
              <input type="date" className={`${inp} w-full mt-1`} value={agreedLast}
                onChange={e => setAgreedLast(e.target.value)} />
              <span className="block text-[11px] text-gray-600 mt-1">
                Leave blank to use the statutory date. Set it if you have both agreed they can go
                earlier — the statutory date is kept too, so the arrangement stays visible.
              </span>
            </label>

            <p className="text-[11px] text-gray-600">
              This records the notice and sets their leaving date. Their login stays on — they are
              working their notice — and the last day appears under “Ending soon” on the dashboard.
              Use End on the day itself.
            </p>
          </div>
        )}

        {kind === 'terms' && c && (
          <div className="space-y-3">
            <div className="flex gap-1 bg-gray-900/60 rounded-lg p-1">
              <button onClick={() => setCorrect(false)}
                className={`flex-1 text-xs py-1.5 rounded ${!correct ? 'bg-blue-600 text-white' : 'text-gray-400'}`}>
                Terms changed
              </button>
              <button onClick={() => setCorrect(true)}
                className={`flex-1 text-xs py-1.5 rounded ${correct ? 'bg-blue-600 text-white' : 'text-gray-400'}`}>
                Fix a mistake
              </button>
            </div>
            <p className="text-[11px] text-gray-600">
              {correct
                ? 'Corrects this period in place — use when the existing record is simply wrong. Nothing new is added to the timeline.'
                : 'Closes the current period and opens a new one, so both sets of terms stay in the history. Use for a real change (temp → permanent, new days).'}
            </p>
            {correct
              ? <label className="block">
                  <span className="text-xs text-gray-500">Start date</span>
                  <input type="date" className={`${inp} w-full mt-1`} value={startDate}
                    onChange={e => setStartDate(e.target.value)} />
                </label>
              : <label className="block">
                  <span className="text-xs text-gray-500">New terms apply from</span>
                  <input type="date" className={`${inp} w-full mt-1`} value={eff}
                    onChange={e => setEff(e.target.value)} />
                </label>}
            {TermsFields}
          </div>
        )}

        {kind === 'rehire' && (
          <div className="space-y-3">
            <p className="text-[11px] text-gray-600">
              {person.needs_setup
                ? 'Employment start is deliberately separate from when the account was created — '
                  + 'someone can start on paper well before they get a login, so record the day they '
                  + 'actually started. Expected hours are generated from this date onward.'
                : 'Starts a new employment period on the existing account and switches the login back '
                  + 'on. Previous periods stay untouched, so the gap in service remains visible.'}
            </p>
            <label className="block">
              <span className="text-xs text-gray-500">Start date</span>
              <input type="date" className={`${inp} w-full mt-1`} value={startDate}
                onChange={e => setStartDate(e.target.value)} />
            </label>
            {TermsFields}
          </div>
        )}

        {kind === 'end' && (
          <div className="space-y-3">
            <p className="text-[11px] text-gray-600">
              Switches the login off and closes the employment period. <span className="text-gray-400">Nothing is
              deleted</span> — every picked, packed and printed record stays, and the username stays claimed so a
              future hire can never inherit this person's history.
            </p>
            {person.needs_offboarding && (
              <p className="text-[11px] text-red-400/90">
                Their last day was {c?.agreed_last_day || c?.statutory_last_day || c?.end_date}
                {' '}and they can still sign in. The date below is their agreed last day — leave it
                as it is unless it was actually different.
              </p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs text-gray-500">Last day</span>
                <input type="date" className={`${inp} w-full mt-1`} value={endDate}
                  onChange={e => setEndDate(e.target.value)} />
              </label>
              <label className="block">
                <span className="text-xs text-gray-500">Reason</span>
                <select className={`${inp} w-full mt-1`} value={reason} onChange={e => setReason(e.target.value)}>
                  {END_REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </label>
            </div>
            <div>
              <span className="text-xs text-gray-500">Leaver checklist</span>
              <div className="mt-1 space-y-1">
                {CHECKLIST.map(([k, l]) => (
                  <label key={k} className="flex items-center gap-2 text-xs text-gray-400">
                    <input type="checkbox" checked={!!checks[k]}
                      onChange={e => setChecks(c2 => ({ ...c2, [k]: e.target.checked }))} />
                    {l}
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {kind !== 'history' && (
          <>
            <label className="block">
              <span className="text-xs text-gray-500">Note (optional)</span>
              <input className={`${inp} w-full mt-1`} value={note} onChange={e => setNote(e.target.value)}
                placeholder="Recorded in the audit trail" />
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={onClose} className={`${btn} bg-gray-800 hover:bg-gray-700 text-gray-300`}>Cancel</button>
              <button onClick={submit} disabled={busy}
                className={`${btn} ${kind === 'end' ? 'bg-red-700 hover:bg-red-600' : 'bg-blue-600 hover:bg-blue-700'} text-white disabled:opacity-50`}>
                {busy ? 'Saving…' : kind === 'end' ? 'End employment'
                  : kind === 'rehire' ? 'Rehire'
                    : kind === 'notice' ? 'Record notice' : 'Save'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
